import { AxiosResponse } from 'axios';
import { OrderConfirmationSearchResponse, OrderConfirmationSearchCriteria, OrderConfirmationAnalysisResponse } from './types';
import { createApiClient } from 'services/apiClient';
import { performBulkDelete, BulkDeleteResult, BulkDeleteOptions } from 'services/bulkDelete';
import { API_BASE_URL } from 'config/api';

const apiClient = createApiClient('ORDER_CONFIRMATION');

export default class OrderConfirmationService {
  static async searchOrderConfirmations(tenantId: string, criteria: OrderConfirmationSearchCriteria): Promise<OrderConfirmationSearchResponse> {
    try {
      console.log('📦 [ORDER_CONFIRMATION] Searching order confirmations:', {
        tenantId,
        criteria,
        url: `${API_BASE_URL}/business-documents/order-confirmations/search/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      
      // Add query parameters
      if (criteria.document_status) params.append('document_status', criteria.document_status);
      if (criteria.created_at_start) params.append('created_at_start', criteria.created_at_start);
      if (criteria.created_at_end) params.append('created_at_end', criteria.created_at_end);
      if (criteria.order_number) params.append('order_number', criteria.order_number);
      if (criteria.document_state_id) params.append('document_state_id', criteria.document_state_id);
      if (criteria.has_been_sent != null) params.append('has_been_sent', criteria.has_been_sent.toString());
      if (criteria.parent_process_state_id) params.append('parent_process_state_id', criteria.parent_process_state_id);
      if (criteria.offer_reference) params.append('offer_reference', criteria.offer_reference);
      if (criteria.max_retry_reached != null) params.append('max_retry_reached', criteria.max_retry_reached.toString());
      if (criteria.has_errors != null) params.append('has_errors', criteria.has_errors.toString());
      if (criteria.has_work_description != null) params.append('has_work_description', criteria.has_work_description.toString());
      if (criteria.days_back) params.append('days_back', criteria.days_back.toString());
      if (criteria.limit) params.append('limit', criteria.limit.toString());
      
      const url = `/business-documents/order-confirmations/search/${tenantId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<OrderConfirmationSearchResponse> = await apiClient.get(url);
      
      console.log('✅ [ORDER_CONFIRMATION] Order confirmation search response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          total_results: response.data.summary.total_results,
          result_count: response.data.results?.length || 0
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER_CONFIRMATION] Error searching order confirmations:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to search order confirmations');
    }
  }

  static async getOrderConfirmationStateDetail(tenantId: string, documentStateId: string): Promise<any> {
    try {
      console.log('📦 [ORDER_CONFIRMATION] Fetching order confirmation state detail:', {
        tenantId,
        documentStateId,
        url: `${API_BASE_URL}/business-documents/order-confirmations/state/${tenantId}/${documentStateId}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<any> = await apiClient.get(`/business-documents/order-confirmations/state/${tenantId}/${documentStateId}`);
      
      // Map the backend response to match the frontend's expected structure
      const mappedData = {
        document_state_id: response.data.document_state_id,
        tenant_id: tenantId,
        order_number: response.data.parsed_document?.order_number,
        document_status: response.data.document_status,
        document_source_type: response.data.document_source_type,
        has_been_sent: response.data.has_been_sent,
        is_processing: response.data.is_processing ?? false,
        has_work_description: response.data.has_work_description,
        work_description_entries: response.data.work_description_entries || [],
        backend_id: response.data.backend_id,
        retry_count: response.data.retry_count,
        max_retry_reached: response.data.max_retry_reached,
        last_error_message: response.data.last_error_message,
        error_history: response.data.error_history,
        parsed_document: response.data.parsed_document,
        parent_process_state_id: response.data.parent_process_state_id,
        parent_process_type: response.data.parent_process_type || null,
        email: response.data.email || null,
        email_eml_path: response.data.email_eml_path,
        doc_path: response.data.doc_path,
        phone_number: response.data.phone_number || null,
        processing_result: response.data.processing_result,
        matched_customer: response.data.processing_result?.matched_customer || response.data.matched_customer || null,
        created_at_date: response.data.created_at_date
      };
      
      console.log('✅ [ORDER_CONFIRMATION] Order confirmation state detail response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: tenantId,
          document_state_id: mappedData.document_state_id,
          document_status: mappedData.document_status,
          order_number: mappedData.parsed_document?.order_number,
          matched_order_items: mappedData.processing_result?.matched_order_items?.length || 0
        }
      });
      
      return mappedData;
    } catch (error: any) {
      console.error('❌ [ORDER_CONFIRMATION] Error fetching order confirmation state detail:', {
        tenantId,
        documentStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch order confirmation state detail');
    }
  }

  static async sendOrderConfirmation(tenantId: string, documentStateId: string, debug: boolean = false): Promise<any> {
    try {
      console.log('📤 [ORDER_CONFIRMATION] Sending order confirmation:', {
        tenantId,
        documentStateId,
        debug,
        url: `${API_BASE_URL}/business-documents/order-confirmations/send/${tenantId}/${documentStateId}`,
        method: 'POST'
      });
      
      const params = new URLSearchParams();
      if (debug) {
        params.append('debug', 'true');
      }
      
      const url = `/business-documents/order-confirmations/send/${tenantId}/${documentStateId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<any> = await apiClient.post(url);
      
      console.log('✅ [ORDER_CONFIRMATION] Send order confirmation response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER_CONFIRMATION] Error sending order confirmation:', {
        tenantId,
        documentStateId,
        debug,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to send order confirmation');
    }
  }

  static async updateOrderConfirmation(tenantId: string, documentStateId: string, orderConfirmationData: any): Promise<any> {
    try {
      console.log('📝 [ORDER_CONFIRMATION] Updating order confirmation:', {
        tenantId,
        documentStateId,
        url: `${API_BASE_URL}/business-documents/order-confirmations/state/${tenantId}/${documentStateId}`,
        method: 'PUT'
      });
      
      const url = `/business-documents/order-confirmations/state/${tenantId}/${documentStateId}`;
      
      const response: AxiosResponse<any> = await apiClient.put(url, orderConfirmationData);
      
      console.log('✅ [ORDER_CONFIRMATION] Update order confirmation response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER_CONFIRMATION] Error updating order confirmation:', {
        tenantId,
        documentStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to update order confirmation');
    }
  }

  static async getOrderConfirmationAnalysis(tenantId: string, days: number = 30): Promise<OrderConfirmationAnalysisResponse> {
    try {
      console.log('📊 [ORDER_CONFIRMATION] Getting order confirmation analysis:', {
        tenantId,
        days,
        url: `${API_BASE_URL}/business-documents/order-confirmations/analysis/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      params.append('days', days.toString());
      
      const url = `/business-documents/order-confirmations/analysis/${tenantId}?${params.toString()}`;
      
      const response: AxiosResponse<OrderConfirmationAnalysisResponse> = await apiClient.get(url);
      
      console.log('✅ [ORDER_CONFIRMATION] Order confirmation analysis response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          total_confirmations: response.data.summary.total_confirmations,
          date_range: response.data.date_range,
          error_percentage: response.data.error_analysis.error_percentage
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [ORDER_CONFIRMATION] Error getting order confirmation analysis:', {
        tenantId,
        days,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to get order confirmation analysis');
    }
  }

  static async deleteOrderConfirmationState(
    tenantId: string,
    documentStateId: string,
    force: boolean = false
  ): Promise<void> {
    try {
      console.log('🗑️ [ORDER_CONFIRMATION] Deleting order confirmation state:', {
        tenantId,
        documentStateId,
        force,
        url: `${API_BASE_URL}/business-documents/order-confirmations/state/${tenantId}/${documentStateId}`,
        method: 'DELETE'
      });
      
      const params = new URLSearchParams();
      if (force) {
        params.append('force', 'true');
      }
      
      const url = `/business-documents/order-confirmations/state/${tenantId}/${documentStateId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<any> = await apiClient.delete(url);
      
      console.log('✅ [ORDER_CONFIRMATION] Delete order confirmation state response:', {
        status: response.status,
        statusText: response.statusText
      });
    } catch (error: any) {
      console.error('❌ [ORDER_CONFIRMATION] Error deleting order confirmation state:', {
        tenantId,
        documentStateId,
        force,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      
      // Re-throw with more detailed error information
      if (error.response?.data?.detail) {
        const errorMessage = new Error(error.response.data.detail);
        (errorMessage as any).status = error.response?.status;
        throw errorMessage;
      }
      throw error;
    }
  }

  static async bulkDeleteOrderConfirmationStates(
    tenantId: string,
    ids: string[],
    options?: BulkDeleteOptions,
  ): Promise<BulkDeleteResult> {
    return performBulkDelete(
      apiClient,
      'ORDER_CONFIRMATION',
      `/business-documents/order-confirmations/state/${tenantId}/bulk-delete`,
      ids,
      options,
    );
  }
}
